import { Component, Input, OnInit } from '@angular/core';
import { Auth } from '@freelancer/auth';
import { Datastore } from '@freelancer/datastore';
import {
  BillingAgreementsCollection,
  CurrenciesIncludingExternalCollection,
  Currency,
  PaymentShareMembersCollection,
  PaymentShareTeam,
  PaymentShareTeamsCollection,
  User,
  UsersCollection,
} from '@freelancer/datastore/collections';
import { ModalRef } from '@freelancer/ui';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { HeadingType } from '@freelancer/ui/heading';
import { Margin } from '@freelancer/ui/margin';
import { FontType, FontWeight, TextSize } from '@freelancer/ui/text';
import { isDefined, toNumber } from '@freelancer/utils';
import {
  PaymentShareEntityTypeApi,
  PaymentShareMemberStatusApi,
} from 'api-typings/users/users';
import * as Rx from 'rxjs';
import { filter, map } from 'rxjs/operators';

@Component({
  selector: 'app-payment-share-member-modal',
  template: `
    <fl-bit *ngIf="isReady$ | flAsync; else loading">
      <fl-sticky-footer-wrapper flTrackingSection="PaymentShareMemberModal">
        <fl-sticky-footer-body>
          <fl-heading
            flTrackingSection="PaymentShareMemberModal"
            [flMarginBottom]="Margin.XXXSMALL"
            [headingType]="HeadingType.H2"
            [size]="TextSize.MID"
          >
            {{ (paymentShareTeam$ | flAsync)?.paymentShareTeamName }}
          </fl-heading>
          <fl-text
            i18n="Payment sharing team owner's information of the team payment"
            [flMarginBottom]="Margin.LARGE"
          >
            Owned by {{ (owner$ | flAsync)?.displayName }}
          </fl-text>
          <fl-text
            [flMarginBottom]="Margin.LARGE"
            i18n="Payment sharing team's information"
          >
            The payment method is owned by
            <fl-text [fontType]="FontType.SPAN" [weight]="FontWeight.BOLD">
              {{ (owner$ | flAsync)?.displayName }}
            </fl-text>
            (@{{ (owner$ | flAsync)?.username }}) who has added you to their
            team. Payments you make using this method will be charged to
            <fl-text [fontType]="FontType.SPAN" [weight]="FontWeight.BOLD">
              {{ (owner$ | flAsync)?.displayName }}
            </fl-text>
            (@{{ (owner$ | flAsync)?.username }})'s payment method. They will be
            notified and can view all transactions made using this payment
            method.
          </fl-text>

          <app-payment-share-member-modal-member-table
            [spendingLimitDuration]="
              (paymentShareTeam$ | flAsync)?.memberSpendingLimitDuration
            "
            [spendingLimitAmount]="
              (paymentShareTeam$ | flAsync)?.memberSpendingLimitAmount
            "
            [isSpendingLimitActive]="
              (paymentShareTeam$ | flAsync)?.isSpendingLimitActive
            "
            [currency]="currency$ | flAsync"
          >
          </app-payment-share-member-modal-member-table>
        </fl-sticky-footer-body>
        <fl-sticky-footer class="ActionContainer">
          <fl-button
            i18n="Payment sharing modal Confirm Button"
            flTrackingLabel="OkButton"
            class="CTAButton"
            [color]="ButtonColor.TRANSPARENT_DARK"
            [size]="ButtonSize.SMALL"
            (click)="handleOk()"
          >
            Ok
          </fl-button>
        </fl-sticky-footer>
      </fl-sticky-footer-wrapper>
    </fl-bit>
    <ng-template #loading>
      <fl-spinner
        flTrackingLabel="PaymentShareMemberModalSpinner"
        [overlay]="true"
      ></fl-spinner>
    </ng-template>
  `,
  styleUrls: ['./payment-share-member-modal.component.scss'],
})
export class PaymentShareMemberModalComponent implements OnInit {
  Margin = Margin;
  HeadingType = HeadingType;
  TextSize = TextSize;
  FontType = FontType;
  FontWeight = FontWeight;
  ButtonSize = ButtonSize;
  ButtonColor = ButtonColor;

  @Input() billingAgreementId: number;

  teamId$: Rx.Observable<number>;
  paymentShareTeam$: Rx.Observable<PaymentShareTeam>;
  currency$: Rx.Observable<Currency | undefined>;
  owner$: Rx.Observable<User | undefined>;
  isReady$: Rx.Observable<boolean>;

  constructor(
    private modalRef: ModalRef<PaymentShareMemberModalComponent>,
    private auth: Auth,
    private datastore: Datastore,
  ) {}

  ngOnInit(): void {
    const paymentShareMembersCollection = this.datastore.collection<
      PaymentShareMembersCollection
    >('paymentShareMembers', query =>
      query
        .where('billingAgreementId', '==', this.billingAgreementId)
        .where(
          'entityId',
          '==',
          this.auth.getUserId().pipe(map(userId => toNumber(userId))),
        )
        .where('entityType', '==', PaymentShareEntityTypeApi.USER)
        .where('status', '==', PaymentShareMemberStatusApi.ADDED),
    );

    const paymentShareMember$ = paymentShareMembersCollection
      .valueChanges()
      .pipe(
        filter(members => members.length > 0),
        map(members => members[0]),
      );

    this.teamId$ = paymentShareMember$.pipe(
      map(member => (member ? member.paymentShareTeamId : undefined)),
      filter(isDefined),
    );

    const paymentShareTeamsCollection = this.datastore.collection<
      PaymentShareTeamsCollection
    >('paymentShareTeams', query => query.where('id', '==', this.teamId$));

    this.paymentShareTeam$ = paymentShareTeamsCollection.valueChanges().pipe(
      filter(paymentTeams => paymentTeams.length > 0),
      map(paymentTeams => paymentTeams[0]),
    );

    const ownerUserId$ = this.paymentShareTeam$.pipe(
      filter(team => team.ownerEntityType === PaymentShareEntityTypeApi.USER),
      map(team => team.ownerEntityId),
    );

    this.owner$ = this.datastore
      .document<UsersCollection>('users', ownerUserId$)
      .valueChanges();

    const billingAgreementId$ = paymentShareMember$.pipe(
      map(member => member.billingAgreementId),
    );

    const billingAgreement$ = this.datastore
      .document<BillingAgreementsCollection>(
        'billingAgreements',
        billingAgreementId$,
      )
      .valueChanges();

    const currencies$ = this.datastore
      .collection<CurrenciesIncludingExternalCollection>(
        'currenciesIncludingExternal',
      )
      .valueChanges();

    this.currency$ = Rx.combineLatest([billingAgreement$, currencies$]).pipe(
      map(([billingAgreement, currencies]) =>
        currencies.find(c => c.id === billingAgreement.currencyId),
      ),
    );

    this.isReady$ = Rx.combineLatest([
      this.paymentShareTeam$,
      paymentShareMember$,
      this.currency$,
    ]).pipe(
      map(([team, member, _]) => {
        // only a team member (not owner) can view the member modal
        if (
          team.ownerEntityId === member.entityId &&
          team.ownerEntityType === member.entityType
        ) {
          throw new Error(`Cannot show this component for the team owner`);
        }
        return true;
      }),
    );
  }

  handleOk() {
    this.modalRef.close();
  }
}
